import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { ProfileService } from 'rainbow-web-sdk/services/core/profile.service';
import { ErrorHelperInfo, ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { LoggerService } from "rainbow-web-sdk/services/core/logger.service";
import { Service } from 'rainbow-web-sdk/services/service';
import { DirectoryInternalSearchResults, DirectorySearchOptions } from 'rainbow-web-sdk/services/core/directorySearch.service';
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { stringifyError } from '../../utils/stringifyError';
import anonymizer from '../../utils/anonymizer';

export const PHONEBOOK_SVC = 'PhonebookService';

export class PhonebookService extends Service {

    private authService: AuthService = AuthService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private profileService: ProfileService = ProfileService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();

    protected constructor() { super(); }

    public static getInstance() {
        return Service.create(PHONEBOOK_SVC, PhonebookService);
    }

    public static build() {
        return new PhonebookService();
    }

    /**
     * This API allows users to search phonebook users by specifying a pattern
     * (API request to {@link https://api.openrainbow.org/search/#api-phonebook-searchInPhonebook Rainbow search portal - phonebook - phonebook - Search in phonebook})
     * @public
     * @description
     * Search contacts in the PBX phonebook the user is connected to by using a specified pattern to filter items.
     * This search is only available if the user has the feature TELEPHONY_PHONE_BOOK in its profile.
     * If specified, the option 'extendedMode' is ignored as the multi-criteria search (associated with feature SEARCH_BY_TAGS) is not available on Phonebook.
     * @param {string} searchPattern Pattern to search
     * @param {DirectorySearchOptions} [options] Search options {@link DirectorySearchOptions}
     * @returns {Promise<DirectoryInternalSearchResults[]>} Resolves with an array of results and the total number of directory items matching the criteria.
     * {@link DirectoryInternalSearchResults}
     * @memberof PhonebookService
     */
    public async searchPBXUsers(searchPattern: string, options?: DirectorySearchOptions): Promise<DirectoryInternalSearchResults> {
        // First, Check user licence
        if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_PHONE_BOOK)) {
            this.logger.debug(`[PhonebookService] searchPBXUsers -- User does not have the profile feature TELEPHONY_PHONE_BOOK`);
            return { contacts: [], total: 0 };
        }
        this.logger.info(`[PhonebookService] search PBX phonebook users with pattern ${anonymizer(searchPattern)}`);

        try {
            // Define useful search options
            const searchOptions: any = {};
            searchOptions.format = "full";

            if (options?.limit) { searchOptions.limit = options.limit; }
            searchOptions.name = searchPattern;

            // Build Request for Server
            const url: URL = new URL(`${config.restServerUrl}/api/rainbow/search/v1.0/phonebooks`);
            const urlParams: string = new URLSearchParams(searchOptions).toString();
            const headers: any = this.authService.getRequestHeader();
            url.search = urlParams;
            const response: Response = await fetch(url.toString(), { method: 'GET', mode: "cors", headers: headers });

            if (!response.ok) { throw (response); }
            const responseData: any = (await response.json());
            const phonebooksData: any = responseData.phoneBooks;

            this.logger.info(`[PhonebookService] search success -- Found ${responseData.total} contact(s)`);

            const phonebookContacts: Contact[] = phonebooksData.map((phonebook: any) => {
                const contact: Contact = this.contactService.createBasicContact("", phonebook.number);
                contact.phonebookDirectoryFlag = true;
                contact.updateName(phonebook.firstName, phonebook.lastName);
                return contact;
            });

            this.logger.info(`[PhonebookService] searchPBXUsers -- Return ${phonebookContacts.length} contact(s)`);

            return { contacts: phonebookContacts, total: responseData.total };
        } catch (error) {
            const errorInfo: ErrorHelperInfo = await this.errorHelperService.getErrorInfo(error, 'searchPBXUsers');
            this.logger.error(`[PhonebookService] ${errorInfo.message}`);
            if (errorInfo.error.status === 403) {
                // Request rejected by server probably because of a user license problem
                return { contacts: [], total: undefined };
            }
            throw errorInfo.error;
        }
    }


    /**
     * This API allows users to search phonebook users by specifying a phone number
     * (API request to {@link https://api.openrainbow.org/search/#api-phonebook-searchInPhonebook Rainbow search portal - phonebook - phonebook - Search in phonebook})
     * @public
     * @description
     * Search contacts in the PBX phonebook the user is connected to by using a specified pattern to filter items on phone number.
     * This search is only available if the user has the feature TELEPHONY_PHONE_BOOK in its profile.
     * @param {string} searchPattern Phone number to search
     * @returns {Promise<Contact[]>} Resolves with an array of {@link Contact} results and
     * the total number of directory items matching the criteria.
     * @memberof PhonebookService
     */

    public async searchByNumber(searchPattern: string): Promise<Contact | null> {
        if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_PHONE_BOOK)) {
            return null;
        }
        this.logger.info(`searchByNumber PBXContact from phonebook with pattern ${searchPattern}`);

        try {
            const url = `${config.restServerUrl}/api/rainbow/search/v1.0/phonebooks?number=${encodeURIComponent(searchPattern)}`;
            const headers = this.authService.getRequestHeader();
            const responseData: any = await fetch(url, { method: "GET", mode: "cors", headers });
            const response: any = await responseData.json();
            const phonebooksData: any = response.phoneBooks[0];
            const phonebookContact: Contact = this.contactService.createBasicContact("", searchPattern);
            phonebookContact.phonebookDirectoryFlag = true;
            if (phonebooksData) {
                phonebookContact.updateName(phonebooksData.firstName, phonebooksData.lastName);
            }
            else {
                phonebookContact.updateName("", searchPattern);
            }
            return phonebookContact;
        } catch (error) {
            this.logger.info(`searchByNumber PBXContact from phonebook with pattern ${searchPattern} --- FAILURE --- ${stringifyError(error)}`);
            return null;
        }
    }
}
